import { Component, Input } from '@angular/core';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { MultiselectAutocompleteModel } from '../../models/multiselect-autocomplete.model';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';

/**
 * ### Accessibility Usage
 * + ***dropdownAriaLabel*** - use this to provide an accessible label for the dropdown. Don't use this if ariaLabelledBy is used
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-multiselect-autocomplete',
    templateUrl: './multiselect-autocomplete.component.html',
    styleUrls: ['./multiselect-autocomplete.component.scss'],
    providers: [getProvider(MultiselectAutocompleteComponent)],
    standalone: false
})
export class MultiselectAutocompleteComponent extends ControlValueAccessorModel<MultiselectAutocompleteModel[]> {

    @Input() id: string = 'multiselectAutocomplete';
    @Input() dropdownAriaLabel: string;
    @Input() showHiddenOption: boolean = false;

    // field object that is currently selected based on dropdown value
    selectedField: MultiselectAutocompleteModel;

    dropdownOptions: DropdownOptionModel[] = [];
    dropdownValue: DropdownOptionModel;

    value: MultiselectAutocompleteModel[];

    disabled: boolean = false;
    hasChips: boolean = false;
    newChip: string;

    readonly buttonTypes = ButtonTypesEnum;

    writeValue(value: MultiselectAutocompleteModel[]) {
        this.value = value;
        this.dropdownOptions = this.value.map(x => new DropdownOptionModel(x.headerLabel, x.type));
        if (!this.dropdownValue && this.dropdownOptions) {
            this.dropdownValue = this.dropdownOptions[0];
            this.selectedField = this.value.find(x => x.type === this.dropdownValue.value);
        }

        this.hasChips = this.value.some(x => x.value.length > 0);

        this.onModelChange(this.value);
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    addChip(chip: any) {
        if (!chip || this.chipIsDuplicate(chip)) {
            return;
        }

        const selectedField = this.value.find(v => v.type === this.selectedField.type);
        if (typeof chip.clone === 'function') {
            selectedField.value.push(chip.clone());

        } else {
            selectedField.value.push(chip);
        }

        this.writeValue(this.value);
    }

    // sets the selectedField value based on dropdown selection
    onDropdownChange(option: DropdownOptionModel): void {
        this.selectedField = this.value.find(x => x.type === option.value);
    }

    removeChip(type: string, chip: any): void {
        const valuesOfType = this.value.find(group => group.type === type).value;
        valuesOfType.splice(valuesOfType.findIndex(chip_ => chip_.id === chip.id), 1);

        this.writeValue(this.value);
    }

    chipIsDuplicate(chip: any): boolean {
        const selectedField = this.value.find(v => v.type === this.selectedField.type);

        for (const value of selectedField.value) {
            if (value.id === chip.id) {
                return true;
            }
        }

        return false;
    }
}
